import { useEffect, useRef, useState } from "react";
import { Calendar, X } from "lucide-react";
import CalendarGrid from "./CalendarGrid";
import {
  formatDisplayDateTime,
  getDefaultPickerMaxYear,
  getDefaultPickerMinYear,
  pad2,
  parseDateTimeValue,
  parseDateValue,
  toDateTimeValue,
  toDateValue,
} from "./datePickerUtils";

const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad2(i));

const INPUT_CLASS =
  "flex items-center gap-2 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white text-left hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500";

export default function DateTimePickerField({
  label,
  value = "",
  onChange,
  required = false,
  placeholder = "Pick date & time",
  min,
  max,
  className = "",
  inputClassName,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const parsed = parseDateTimeValue(value);
  const datePart = value?.includes("T") ? value.split("T")[0] : value?.slice(0, 10) || "";
  const timePart = value?.includes("T") ? value.split("T")[1]?.slice(0, 5) : "09:00";
  const selectedDate = parseDateValue(datePart) || parsed;
  const initial = selectedDate || new Date();
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [draftDate, setDraftDate] = useState(datePart);
  const [draftHour, setDraftHour] = useState(timePart?.split(":")[0] || "09");
  const [draftMinute, setDraftMinute] = useState(timePart?.split(":")[1] || "00");

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setDraftDate(datePart);
      const [h, m] = (timePart || "09:00").split(":");
      setDraftHour(pad2(Number(h) || 9));
      setDraftMinute(pad2(Number(m) || 0));
      if (selectedDate) {
        setViewYear(selectedDate.getFullYear());
        setViewMonth(selectedDate.getMonth());
      }
    }
  }, [open, datePart, timePart, selectedDate]);

  const inRange = (date) => {
    const v = toDateValue(date);
    if (min && v < min) return false;
    if (max && v > max) return false;
    return true;
  };

  const today = new Date();
  const minYear = min
    ? parseDateValue(min)?.getFullYear()
    : getDefaultPickerMinYear(today);
  const maxYear = max
    ? parseDateValue(max)?.getFullYear()
    : getDefaultPickerMaxYear(today);

  const apply = (d = draftDate, h = draftHour, m = draftMinute) => {
    if (!d) return;
    const dt = new Date(`${d}T${h}:${m}`);
    if (Number.isNaN(dt.getTime())) return;
    onChange?.(toDateTimeValue(dt));
    setOpen(false);
  };

  const btnClass =
    inputClassName ||
    (compact
      ? "flex items-center gap-1.5 w-full border border-slate-300 rounded-lg px-2 py-1.5 text-sm bg-white text-left hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
      : INPUT_CLASS);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-1">
          {label}
          {required ? " *" : ""}
        </label>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${btnClass} ${!value ? "text-slate-400" : "text-slate-800"}`}
      >
        <Calendar className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-teal-600 shrink-0`} />
        <span className="flex-1 truncate">{value ? formatDisplayDateTime(value) : placeholder}</span>
        {value && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange?.("");
            }}
            className="p-0.5 rounded hover:bg-slate-100 text-slate-400"
            aria-label="Clear"
          >
            <X className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {open && (
        <div className="absolute z-50 mt-2 left-0 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 w-[300px]">
          <CalendarGrid
            viewYear={viewYear}
            viewMonth={viewMonth}
            selectedDate={parseDateValue(draftDate)}
            onSelectDate={(date) => {
              if (!inRange(date)) return;
              setDraftDate(toDateValue(date));
            }}
            onViewMonthChange={(y, m) => {
              setViewYear(y);
              setViewMonth(m);
            }}
            showYearMonthSelect
            minYear={minYear}
            maxYear={maxYear}
            isDateDisabled={(date) => !inRange(date)}
          />

          <p className="text-[11px] font-medium text-slate-400 mt-3 mb-1">Time</p>
          <div className="flex gap-2">
            <div className="flex-1 h-28 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-1 space-y-0.5">
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setDraftHour(h)}
                  className={`w-full py-1 rounded-md text-xs font-medium ${
                    draftHour === h ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-teal-50"
                  }`}
                >
                  {h}
                </button>
              ))}
            </div>
            <div className="flex-1 h-28 overflow-y-auto rounded-lg border border-slate-100 bg-slate-50/50 p-1 space-y-0.5">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDraftMinute(m)}
                  className={`w-full py-1 rounded-md text-xs font-medium ${
                    draftMinute === m ? "bg-teal-600 text-white" : "text-slate-700 hover:bg-teal-50"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between gap-2 mt-3 pt-3 border-t border-slate-100">
            <button
              type="button"
              onClick={() => onChange?.("")}
              className="text-xs font-medium text-slate-500 px-2 py-1 rounded-lg hover:bg-slate-50"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => {
                const now = new Date();
                apply(toDateValue(now), pad2(now.getHours()), pad2(now.getMinutes()));
              }}
              className="text-xs font-medium text-teal-700 px-2 py-1 rounded-lg bg-teal-50 hover:bg-teal-100"
            >
              Now
            </button>
            <button
              type="button"
              onClick={() => apply()}
              disabled={!draftDate}
              className="text-xs font-medium text-white px-3 py-1 rounded-lg bg-teal-600 hover:bg-teal-700 disabled:opacity-50"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
